import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import useEditMode from '../hooks/useEditMode';
import { useLang } from '../context/LangContext';
import { UI, DEFAULTS_AR, DEFAULTS_KU } from '../i18n/translations';
import ServiceModal from '../components/ServiceModal';
import Marquee from '../components/Marquee';
import FadeInSection from '../components/FadeInSection';
import ConsultationForm from '../components/ConsultationForm';
import '../css/service-modal.css';
import '../css/animations.css';
import { db, storage } from '../firebase';
import { collection, addDoc, getDocs, onSnapshot, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../css/home.css';
import '../css/about.css';
import '../css/services.css';
import '../css/portfolio.css';
import '../css/reviews.css';
import '../css/contact.css';

// Click-to-edit text field
function EditField({ tag: Tag = 'span', value, onChange, className, style }) {
  const ref = useRef();
  useEffect(() => {
    if (ref.current && ref.current.innerText !== (value ?? '')) ref.current.innerText = value ?? '';
  }, [value]);
  return (
    <Tag ref={ref} contentEditable suppressContentEditableWarning
      className={`amko-editable${className ? ' ' + className : ''}`} style={style}
      onBlur={e => onChange && onChange(e.currentTarget.innerText)} />
  );
}



function getPortfolioImages(item) {
  return item.images?.length ? item.images : item.imageUrl ? [item.imageUrl] : [];
}

// Pure function outside component — not recreated on every render
function getCardTransform(offset, mobile) {
  const abs = Math.abs(offset);
  if (abs > 2) return null;
  const sign  = offset < 0 ? -1 : offset > 0 ? 1 : 0;
  const tx    = offset * 54;
  const scale = abs === 0 ? 1 : abs === 1 ? 0.8 : 0.62;
  const zi    = 10 - abs * 3;
  const op    = abs === 0 ? 1 : abs === 1 ? 0.88 : 0.55;
  // translateZ(0) must be in the JS string — inline style overwrites the CSS rule
  const transform = mobile
    ? `translateX(${tx}%) scale(${scale}) translateZ(0)`
    : `translateX(${tx}%) rotateY(${sign * -42 * Math.min(abs, 1)}deg) scale(${scale}) translateZ(0)`;
  return { transform, zIndex: zi, opacity: op };
}

const PortfolioCoverflow = memo(function PortfolioCoverflow({ items, onSelect }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused]       = useState(false);
  const touchStartRef = useRef(null);   // ref instead of state — no re-render on touch
  const prevLenRef    = useRef(items.length);
  const isMobile      = useRef(typeof window !== 'undefined' && window.innerWidth <= 768);
  const n = items.length;

  useEffect(() => {
    if (prevLenRef.current !== n) { setActiveIdx(0); prevLenRef.current = n; }
  }, [n]);

  // Preload adjacent images
  useEffect(() => {
    if (n === 0) return;
    [-1, 0, 1, 2].forEach(off => {
      const item = items[((activeIdx + off) % n + n) % n];
      const imgs = getPortfolioImages(item);
      if (imgs[0]) { const img = new Image(); img.src = imgs[0]; }
    });
  }, [activeIdx, items, n]);

  useEffect(() => {
    if (paused || n <= 1) return;
    const t = setInterval(() => setActiveIdx(i => (i + 1) % n), 3800);
    return () => clearInterval(t);
  }, [paused, n]);

  // Always use functional updates so stale closure can never cause wrong index
  const prev = useCallback(() => {
    setPaused(true);
    setActiveIdx(i => (i - 1 + n) % n);
  }, [n]);

  const next = useCallback(() => {
    setPaused(true);
    setActiveIdx(i => (i + 1) % n);
  }, [n]);

  const goTo = useCallback(idx => {
    setActiveIdx(((idx % n) + n) % n);
  }, [n]);

  if (n === 0) return null;

  return (
    <div className="cf-wrapper"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={e => {
        // Don't track swipe when tapping arrow/dot buttons — let their onClick fire normally
        if (e.target.closest('button')) return;
        touchStartRef.current = e.touches[0].clientX;
      }}
      onTouchEnd={e => {
        if (touchStartRef.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartRef.current;
        touchStartRef.current = null;
        if (Math.abs(dx) > 40) {
          setPaused(true);
          // Functional update — immune to stale activeIdx from autoplay race
          setActiveIdx(i => dx < 0 ? (i + 1) % n : (i - 1 + n) % n);
        }
      }}>

      <div className="cf-stage">
        {items.map((item, i) => {
          let offset = i - activeIdx;
          if (offset >  n / 2) offset -= n;
          if (offset < -n / 2) offset += n;
          const style = getCardTransform(offset, isMobile.current);
          if (!style) return null;
          const isActive = offset === 0;
          const imgs = getPortfolioImages(item);
          const cover = imgs[0];
          return (
            <div key={item.id}
              className={`cf-card${isActive ? ' cf-card-active' : ''}`}
              style={style}
              onClick={() => isActive ? onSelect(item) : goTo(i)}>
              <div className="cf-card-img-wrap">
                {cover
                  ? <img
                      src={cover}
                      alt={item.title}
                      className="cf-card-img"
                      loading={Math.abs(offset) <= 1 ? 'eager' : 'lazy'}
                      decoding="async"
                      onContextMenu={e => e.preventDefault()}
                    />
                  : <div className="cf-card-no-img">🖼️</div>}
              </div>
              {imgs.length > 1 && <span className="cf-card-count">+{imgs.length}</span>}
            </div>
          );
        })}
      </div>

      {/* touch-action: manipulation removes 300ms tap delay on mobile */}
      <button className="cf-arr cf-arr-l" style={{ touchAction: 'manipulation' }} onClick={prev}>‹</button>
      <button className="cf-arr cf-arr-r" style={{ touchAction: 'manipulation' }} onClick={next}>›</button>

      <div className="cf-dots">
        {items.map((_, i) => (
          <button key={i} className={`cf-dot${i === activeIdx ? ' active' : ''}`}
            style={{ touchAction: 'manipulation' }}
            onClick={() => { setPaused(true); goTo(i); }} />
        ))}
      </div>

      <div className="cf-progress-track">
        <div key={`${activeIdx}-${paused}`}
          className={`cf-progress-fill${paused ? ' paused' : ''}`} />
      </div>
    </div>
  );
});

function PortfolioModal({ item, onClose }) {
  const [idx, setIdx] = useState(0);
  const imgs = getPortfolioImages(item);
  const prev = e => { e.stopPropagation(); setIdx(i => (i - 1 + imgs.length) % imgs.length); };
  const next = e => { e.stopPropagation(); setIdx(i => (i + 1) % imgs.length); };

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft')  setIdx(i => (i - 1 + imgs.length) % imgs.length);
      if (e.key === 'ArrowRight') setIdx(i => (i + 1) % imgs.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [imgs.length, onClose]);

  return (
    <div className="pf-overlay" onClick={onClose}>
      <div className="pf-modal" onClick={e => e.stopPropagation()}>
        {/* Carousel */}
        <div className="pf-modal-carousel">
          <button className="pf-close" onClick={onClose}>✕</button>
          {imgs.length > 0 ? (
            <img key={idx} src={imgs[idx]} alt={item.title} className="pf-modal-img" onContextMenu={e => e.preventDefault()} />
          ) : (
            <div className="pf-modal-no-img">🖼️</div>
          )}
          {imgs.length > 1 && (
            <>
              <button className="pf-arr pf-arr-l" onClick={prev}>‹</button>
              <button className="pf-arr pf-arr-r" onClick={next}>›</button>
              <div className="pf-dots">
                {imgs.map((_, i) => (
                  <button key={i} className={`pf-dot ${i === idx ? 'active' : ''}`} onClick={e => { e.stopPropagation(); setIdx(i); }} />
                ))}
              </div>
              <span className="pf-counter">{idx + 1} / {imgs.length}</span>
            </>
          )}
        </div>
        {/* Thumbnails */}
        {imgs.length > 1 && (
          <div className="pf-thumbs">
            {imgs.map((url, i) => (
              <div key={i} className={`pf-thumb ${i === idx ? 'active' : ''}`} onClick={() => setIdx(i)}>
                <img src={url} alt="" onContextMenu={e => e.preventDefault()} />
              </div>
            ))}
          </div>
        )}
        {/* Minimal info — only show if description exists */}
        {item.description && (
          <div className="pf-modal-body" style={{ padding: '1rem 1.5rem' }}>
            <p className="pf-modal-desc">{item.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}

const DEFAULTS = {
  hero:     { tag: 'Creative Agency — Erbil', line1: 'Marketing that Moves.', line2: 'Events that Inspire.', subtitle: 'Based in Erbil — we help businesses grow through branding, digital marketing, media production, and unforgettable events.', bullets: ['Creative & modern solutions for local and regional markets', 'Professional event planning & execution from start to finish', 'High-quality visual production & impactful storytelling', 'End-to-end branding and event services under one agency'], stats: [{ num: '5+', label: 'Years' }, { num: '100+', label: 'Projects' }, { num: '50+', label: 'Clients' }] },
  about:    { p1: 'AMKO Agency combines creativity, technology, and market insight to deliver modern branding experiences that connect businesses with their audiences.', p2: 'We work with startups, NGOs, corporate businesses, restaurants, retail brands, and entrepreneurs across Erbil and the Kurdistan region.', vision: 'To become one of the leading creative agencies in Iraq and the Middle East.', mission: 'Empowering businesses through strategic branding, creative storytelling, and digital transformation.', stat: '5+', statLabel: 'Years creating impact in Erbil', values: ['✦ Creativity', '⚡ Innovation', '💎 Professionalism', '🤝 Transparency', '🎯 Quality First', '🚀 Client Success'] },
  services: { items: [{ icon: '🎨', title: 'Branding & Identity', items: ['Logo Design', 'Brand Guidelines', 'Visual Identity Development', 'Packaging Design', 'Brand Positioning'] }, { icon: '📱', title: 'Digital Marketing', items: ['Social Media Management', 'Social Media Advertising', 'Content Creation', 'Campaign Management', 'Influencer Marketing'] }, { icon: '💻', title: 'Website & Digital', items: ['Website Design & Development', 'Landing Pages', 'UI/UX Design', 'SEO Optimization', 'Digital Consultation'] }, { icon: '🎬', title: 'Media Production', items: ['Photography', 'Videography', 'Drone Filming', 'Promotional Videos', 'Documentary Production'] }, { icon: '🎪', title: 'Event Management', items: ['Corporate Events & Conferences', 'Product Launches', 'Festivals & Public Events', 'Weddings & Private Events', 'Stage & Venue Design'] }, { icon: '🖼️', title: 'Graphic Design', items: ['Promotional Designs', 'Brochures & Flyers', 'Billboard & Outdoor Ads', 'Event Branding', 'Corporate Profiles'] }] },
  process:  { steps: [{ n: '01', title: 'Reservation', desc: 'Book a consultation online' }, { n: '02', title: 'Consultation', desc: 'Understand your goals' }, { n: '03', title: 'Planning', desc: 'Build the strategy' }, { n: '04', title: 'Execution', desc: 'Deliver with care' }, { n: '05', title: 'Results', desc: 'Measure & grow' }] },
  clients:  { list: ['Cultural & Social Center', 'Hawler Restaurant', 'Bashar E.S.Co', "Mar Qardakh Int'l School", 'Ankawa Opportunities', 'Merqamina Private School', 'Galaxy', 'Smashlab', 'Canto Del Mare', 'Dewilux', 'Casati Boya', 'Klimatherm', 'Twig'] },
  faqs:     { items: [{ q: 'What services does AMKO offer?', a: 'Branding, digital marketing, website development, media production, event management, and graphic design.' }, { q: 'How do I book a consultation?', a: 'Click "Book Consultation" and fill in the form. We confirm within 24 hours.' }, { q: 'How long does a project take?', a: 'Logo: 5–10 days. Full brand identity: 2–4 weeks. Website: 4–8 weeks.' }, { q: 'Do you work outside Erbil?', a: 'Yes. We serve clients across Kurdistan, Iraq, and internationally for digital services.' }] },
  contact:  { phone: '+964 778 142 8130', email: 'Info@amkoiq.com', location: 'Erbil, Kurdistan Region, Iraq', whatsapp: 'https://wa.me/9647781428130' },
};

const SECTIONS = ['hero', 'about', 'services', 'process', 'clients', 'faqs', 'contact'];

function PdfViewer() {
  const [loaded, setLoaded] = useState(false);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'1rem', padding:'1.5rem 1.25rem' }}>
        <div style={{ fontSize:'2.5rem', lineHeight:1 }}>📄</div>
        <a href="/PDFamko.pdf" target="_blank" rel="noreferrer"
          style={{ display:'block', width:'100%', background:'#F5A623', color:'#1a0a00', padding:'0.8rem 1rem', borderRadius:10, fontWeight:800, textDecoration:'none', fontSize:'0.95rem', textAlign:'center', boxShadow:'0 4px 16px rgba(245,166,35,0.4)' }}>
          Open PDF ↗
        </a>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!loaded && (
        <div className="pdf-loading">
          <div className="pdf-spinner" />
          <span>Loading PDF…</span>
        </div>
      )}
      <iframe
        src="/PDFamko.pdf#toolbar=0&navpanes=0&scrollbar=1"
        title="AMKO Agency Company Profile"
        className="pdf-iframe"
        style={{ opacity: loaded ? 1 : 0 }}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}


// Unsplash search terms tuned per service for more relevant results
const UNSPLASH_QUERIES = {
  'Branding & Identity': 'brand identity designer creative studio',
  'Digital Marketing':   'social media marketing team office',
  'Website & Digital':   'web designer ux ui laptop screen',
  'Media Production':    'video production camera crew studio',
  'Event Management':    'corporate event conference hall audience',
  'Graphic Design':      'graphic designer creative workspace printing',
};

// Cache fetched URLs in memory so we only hit the API once per session
const unsplashCache = {};

function ServiceCard({ s, onClick, lang }) {
  const wrapRef      = useRef(null);
  const mediaWrapRef = useRef(null);
  const videoRef     = useRef(null);
  const imgRef       = useRef(null);
  const [fallbackImg, setFallbackImg] = useState(null);

  const media = useMemo(() => [
    ...(s.videos || []).map(url => ({ type: 'video', url })),
    ...(s.images?.length ? s.images : s.imageUrl ? [s.imageUrl] : []).map(url => ({ type: 'image', url })),
  ], [s.videos, s.images, s.imageUrl]);

  // Auto-fetch a relevant Unsplash photo when no media has been uploaded yet
  useEffect(() => {
    if (media.length > 0) return;
    const key = process.env.REACT_APP_UNSPLASH_KEY;
    if (!key || key === 'your_unsplash_access_key_here') return;

    const cacheKey = s.title;
    if (unsplashCache[cacheKey]) { setFallbackImg(unsplashCache[cacheKey]); return; }

    const query = encodeURIComponent(UNSPLASH_QUERIES[s.title] || s.title);
    fetch(`https://api.unsplash.com/search/photos?query=${query}&per_page=1&orientation=landscape&client_id=${key}`)
      .then(r => r.json())
      .then(data => {
        const url = data?.results?.[0]?.urls?.regular;
        if (url) { unsplashCache[cacheKey] = url; setFallbackImg(url); }
      })
      .catch(() => {});
  }, [media.length, s.title]);

  const [idx, setIdx] = useState(0);
  const idxRef = useRef(0);

  // Preload images
  useEffect(() => {
    media.forEach(m => { if (m.type === 'image') { const i = new Image(); i.src = m.url; } });
  }, [media]);

  // Create video element fully imperatively so all HTML attributes are set before DOM insertion
  useEffect(() => {
    const container = mediaWrapRef.current;
    if (!container) return;

    const firstVid = media.find(m => m.type === 'video');
    if (!firstVid) return;

    // Build element with all attributes before appending — critical for Chrome Android
    const vid = document.createElement('video');
    vid.setAttribute('autoplay', '');
    vid.setAttribute('muted', '');
    vid.setAttribute('loop', '');
    vid.setAttribute('playsinline', '');
    vid.setAttribute('preload', 'metadata');
    vid.setAttribute('controlslist', 'nodownload noremoteplayback');
    vid.muted = true;
    vid.defaultMuted = true;
    vid.src = firstVid.url;
    vid.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;';
    vid.addEventListener('contextmenu', e => e.preventDefault());

    container.appendChild(vid);
    videoRef.current = vid;

    vid.play().catch(() => {
      // Retry on first user interaction (fallback for strict browsers)
      const retry = () => { vid.play().catch(() => {}); document.removeEventListener('touchstart', retry); };
      document.addEventListener('touchstart', retry, { once: true });
    });

    // IntersectionObserver — pause when off-screen
    const obs = new IntersectionObserver(([entry]) => {
      if (!videoRef.current) return;
      if (entry.isIntersecting && media[idxRef.current]?.type === 'video') {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }, { threshold: 0.2 });
    obs.observe(container);

    return () => {
      obs.disconnect();
      vid.pause();
      vid.src = '';
      container.removeChild(vid);
      videoRef.current = null;
    };
  }, [media]);

  const goTo = useCallback((newIdx) => {
    idxRef.current = newIdx;
    setIdx(newIdx);
    const cur = media[newIdx];
    const vid = videoRef.current;
    const img = imgRef.current;
    if (!vid || !img) return;

    if (cur?.type === 'video') {
      if (vid.src !== cur.url) { vid.src = cur.url; vid.load(); }
      vid.style.display = 'block';
      img.style.display = 'none';
      vid.play().catch(() => {});
    } else {
      vid.pause();
      vid.style.display = 'none';
      img.src = cur?.url || '';
      img.style.display = 'block';
    }
  }, [media]);

  const firstItem = media[0];
  const prev = e => { e.stopPropagation(); goTo((idx - 1 + media.length) % media.length); };
  const next = e => { e.stopPropagation(); goTo((idx + 1) % media.length); };

  return (
    <div ref={wrapRef} className="service-card-full reveal" onClick={onClick}>
      <div className="service-card-media">

        {/* Video injected imperatively into this div */}
        <div ref={mediaWrapRef} style={{ position: 'absolute', inset: 0,
          display: firstItem?.type === 'video' ? 'block' : 'none' }} />

        {/* Image — custom upload or Unsplash fallback */}
        <img
          ref={imgRef}
          src={firstItem?.type === 'image' ? firstItem.url : null}
          alt={s.title}
          style={{ display: firstItem?.type === 'image' ? 'block' : 'none',
                   position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          onContextMenu={e => e.preventDefault()}
        />

        {/* Unsplash auto-photo when no custom media */}
        {!firstItem && fallbackImg && (
          <img
            src={fallbackImg}
            alt={s.title}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onContextMenu={e => e.preventDefault()}
          />
        )}

        {!firstItem && !fallbackImg && <div className="service-card-no-img">{s.icon}</div>}
        <div className="service-card-gradient" />

        {media.length > 1 && (
          <>
            <button className="scard-arr scard-arr-l" onClick={prev}>‹</button>
            <button className="scard-arr scard-arr-r" onClick={next}>›</button>
            <div className="scard-dots">
              {media.map((m, i) => (
                <span key={i}
                  className={`scard-dot ${i === idx ? 'active' : ''} ${m.type === 'video' ? 'scard-dot-v' : ''}`}
                  onClick={e => { e.stopPropagation(); goTo(i); }}
                />
              ))}
            </div>
          </>
        )}

        <div className="service-card-overlay" />
      </div>

      <div className="service-card-body">
        <div className="service-card-top">
          <span className="service-icon-lg">{s.icon}</span>
          <h3>{s.title}</h3>
        </div>
        <ul>{(s.items || []).slice(0, 4).map(i => <li key={i}><span>—</span>{i}</li>)}</ul>
        <div className="service-card-cta-row">
          <span className="service-card-cta">{media[idx]?.type === 'video' ? (lang === 'ar' ? 'شاهد واستكشف' : 'Watch & explore') : (lang === 'ar' ? 'اعرف أكثر' : 'View details')}</span>
          <span className="service-card-cta-arrow">→</span>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { lang } = useLang();
  const t = UI[lang];
  const [content, setContent] = useState(DEFAULTS);
  const { editMode, notifySection } = useEditMode();

  // Helper: update a simple field and notify admin panel
  const edit = useCallback((section, key, val) => {
    setContent(p => {
      const updated = { ...p, [section]: { ...p[section], [key]: val } };
      notifySection(section, updated[section]);
      return updated;
    });
  }, [notifySection]);

  // Upload image to Storage and update a section field
  const uploadImg = useCallback(async (file, section, key, transform) => {
    const r = storageRef(storage, `${section}/${Date.now()}_${file.name}`);
    await uploadBytes(r, file);
    const url = await getDownloadURL(r);
    setContent(p => {
      const val = transform ? transform(p[section], url) : url;
      const updated = { ...p, [section]: { ...p[section], [key]: val } };
      notifySection(section, updated[section]);
      return updated;
    });
  }, [notifySection]);

  // Service card helpers
  const editService = useCallback((i, key, val) => {
    setContent(p => {
      const items = [...(p.services?.items || [])];
      items[i] = { ...items[i], [key]: val };
      const updated = { ...p, services: { ...p.services, items } };
      notifySection('services', updated.services);
      return updated;
    });
  }, [notifySection]);

  const editServiceItem = useCallback((si, ii, val) => {
    setContent(p => {
      const items = [...(p.services?.items || [])];
      const its = [...(items[si].items || [])];
      its[ii] = val;
      items[si] = { ...items[si], items: its };
      const updated = { ...p, services: { ...p.services, items } };
      notifySection('services', updated.services);
      return updated;
    });
  }, [notifySection]);

  const uploadServiceImg = useCallback(async (file, i) => {
    const r = storageRef(storage, `services/${i}_${Date.now()}_${file.name}`);
    await uploadBytes(r, file);
    const url = await getDownloadURL(r);
    setContent(p => {
      const items = [...(p.services?.items || [])];
      const existing = items[i].images || (items[i].imageUrl ? [items[i].imageUrl] : []);
      items[i] = { ...items[i], images: [...existing, url], imageUrl: '' };
      const updated = { ...p, services: { ...p.services, items } };
      notifySection('services', updated.services);
      return updated;
    });
  }, [notifySection]);

  // Apply edit-mode class to body so CSS highlights activate
  useEffect(() => {
    if (editMode) document.body.classList.add('amko-edit-mode');
    return () => document.body.classList.remove('amko-edit-mode');
  }, [editMode]);
  const [reviews, setReviews] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [faqOpen, setFaqOpen] = useState(null);
  const [bookForm, setBookForm] = useState({ fullName: '', email: '', phone: '', service: '', date: '', time: '', notes: '' });
  const [bookSent, setBookSent] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const [showPdf, setShowPdf]   = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: '', role: '', rating: 0, text: '' });
  const [reviewSent, setReviewSent] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const [activePortfolio, setActivePortfolio] = useState(null);
  const closeService = useCallback(() => setActiveService(null), []);
  const [valuesPos, setValuesPos] = useState({ x: 0, y: 0 });
  const [cardPos, setCardPos]     = useState({ x: 0, y: 0 });
  const [cardSize, setCardSize]   = useState({ w: 640, h: 520 });
  const [valuesW, setValuesW]     = useState(100);

  const [showClients, setShowClients] = useState(true);

  const heroRef    = useRef(null);
  const heroTagRef = useRef(null);
  const heroH1Ref  = useRef(null);
  const heroSubRef = useRef(null);

  useEffect(() => {
    // Real-time site content — updates instantly when admin deploys any section
    const unsubContent = SECTIONS.map(s =>
      onSnapshot(doc(db, 'siteContent', s), snap => {
        setContent(prev => ({ ...prev, [s]: snap.exists() ? snap.data() : DEFAULTS[s] }));
      }, () => {})
    );

    const unsubLayout = onSnapshot(doc(db, 'siteContent', 'layout'), snap => {
      if (!snap.exists()) return;
      const l = snap.data();
      if (l.valuesX !== undefined) setValuesPos({ x: l.valuesX, y: l.valuesY });
      if (l.cardX   !== undefined) setCardPos({ x: l.cardX, y: l.cardY });
      if (l.cardW   !== undefined) setCardSize({ w: l.cardW, h: l.cardH ?? 520 });
      if (l.valuesW !== undefined) setValuesW(l.valuesW);
    }, () => {});

    const unsubSettings = onSnapshot(doc(db, 'siteContent', 'settings'), snap => {
      if (snap.exists() && snap.data().showClients === false) setShowClients(false);
      else setShowClients(true);
    }, () => {});

    // Real-time reviews
    const unsubReviews = onSnapshot(collection(db, 'reviews'), snap => {
      const approved = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => r.approved === true)
        .sort((a, b) => {
          const ta = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const tb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return tb - ta;
        });
      setReviews(approved);
    }, () => {});

    // Load portfolio items
    getDocs(query(collection(db, 'portfolio'), orderBy('createdAt', 'desc')))
      .then(snap => setPortfolio(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});

    return () => {
      unsubContent.forEach(u => u());
      unsubLayout();
      unsubSettings();
      unsubReviews();
    };
  }, []);

  // Hero entry animation: triggers on load, scroll-back into view, and bfcache restore
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const bgEl = hero.querySelector('.hero-bg');
    let debounceTimer = null;
    let visible = false;

    const trigger = () => {
      // Clear scroll-parallax inline transforms so animations start from their initial state
      [heroTagRef, heroH1Ref, heroSubRef].forEach(r => { if (r.current) r.current.style.transform = ''; });
      hero.style.opacity = '';
      // Restart Ken Burns
      if (bgEl) { bgEl.style.animation = 'none'; void bgEl.offsetHeight; bgEl.style.animation = ''; }
      // Restart text animations via class toggle
      hero.classList.remove('hero-anim');
      void hero.offsetHeight;
      hero.classList.add('hero-anim');
    };

    const obs = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !visible) {
          visible = true;
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(trigger, 100);
        } else if (!entry.isIntersecting && visible) {
          visible = false;
          clearTimeout(debounceTimer);
          hero.classList.remove('hero-anim');
        }
      });
    }, { threshold: 0.1 });

    obs.observe(hero);
    const onPageShow = e => { if (e.persisted) { visible = false; trigger(); } };
    window.addEventListener('pageshow', onPageShow);
    return () => { obs.disconnect(); clearTimeout(debounceTimer); window.removeEventListener('pageshow', onPageShow); };
  }, []);

  useEffect(() => {
    const timers = new Map();
    const obs = new IntersectionObserver(entries => {
      entries.forEach((e, i) => {
        if (e.isIntersecting) {
          const el = e.target;
          const t = setTimeout(() => {
            el.classList.add('revealed');
            obs.unobserve(el); // animate once only
          }, i * 80);
          timers.set(el, t);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' });
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => { obs.disconnect(); timers.forEach(clearTimeout); };
  }, []);

  const submitBook = async e => {
    e.preventDefault();
    await addDoc(collection(db, 'reservations'), { ...bookForm, status: 'pending', createdAt: serverTimestamp() });

    const formData = new FormData();
    formData.append('access_key', process.env.REACT_APP_WEB3FORMS_KEY);
    formData.append('subject', `📅 New Booking — ${bookForm.fullName} (${bookForm.service})`);
    formData.append('from_name', 'AMKO Agency Website');
    formData.append('replyto', bookForm.email);
    formData.append('Client Name',    bookForm.fullName);
    formData.append('Client Email',   bookForm.email);
    formData.append('Client Phone',   bookForm.phone);
    formData.append('Service',        bookForm.service);
    formData.append('Preferred Date', bookForm.date);
    formData.append('Preferred Time', bookForm.time);
    formData.append('Notes',          bookForm.notes || '—');

    fetch('https://api.web3forms.com/submit', { method: 'POST', body: formData }).catch(() => {});

    setBookSent(true);
  };

  const submitReview = async e => {
    e.preventDefault();
    if (!reviewForm.rating) return alert('Please select a rating');
    await addDoc(collection(db, 'reviews'), { ...reviewForm, approved: false, createdAt: serverTimestamp() });
    setReviewSent(true);
  };


  useEffect(() => {
    if (window.innerWidth < 768) return;
    const hero = heroRef.current;
    if (!hero) return;

    // Cache once — reading offsetHeight inside scroll causes layout reflow every frame
    let heroH = hero.offsetHeight;
    let rafId = null;

    const onResize = () => { heroH = hero.offsetHeight; };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const sy = window.scrollY;
        const p  = Math.min(sy / heroH, 1);
        // Use translate3d so the browser stays on the compositor thread
        hero.style.opacity = String(1 - p * 0.6);
        if (heroTagRef.current) heroTagRef.current.style.transform = `translate3d(${sy * -0.08}px,0,0)`;
        if (heroH1Ref.current)  heroH1Ref.current.style.transform  = `translate3d(${sy * -0.04}px,0,0)`;
        if (heroSubRef.current) heroSubRef.current.style.transform  = `translate3d(${sy *  0.04}px,0,0)`;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const displayReviews = reviews;
  const activeDefaults = useMemo(() => lang === 'ar' ? DEFAULTS_AR : lang === 'ku' ? DEFAULTS_KU : DEFAULTS, [lang]);
  const activeContent  = useMemo(() => lang === 'ar' ? DEFAULTS_AR : lang === 'ku' ? DEFAULTS_KU : content, [lang, content]);
  const { hero, about, services, process: proc, clients, faqs, contact } = activeContent;
  const serviceItems = useMemo(() => services?.items || activeDefaults.services.items, [services, activeDefaults]);
  const filteredPortfolio = portfolio;

  return (
    <div className="home">
      {editMode && <div className="amko-edit-badge">✏️ Edit Mode — click any text to edit</div>}

      {/* HERO */}
      <section className="hero" id="home" ref={heroRef}>
        <div className="hero-bg" />
        <div className="hero-overlay" />
        <div className="hero-dots" aria-hidden="true" />
        <div className="hero-deco-1" aria-hidden="true" />
        <div className="hero-deco-2" aria-hidden="true" />

        <div className="hero-scroll-hint" aria-hidden="true">
          <span className="hero-scroll-label">{t.heroScroll}</span>
          <div className="hero-scroll-track" />
        </div>

        <div className="hero-inner">
          {/* LEFT */}
          <div className="hero-left">
            <div className="hero-tag" ref={heroTagRef}>
              <span className="hero-tag-dot" />
              {editMode
                ? <EditField value={hero.tag} onChange={v => edit('hero','tag',v)} />
                : <span>{hero.tag}</span>}
            </div>
            <h1 ref={heroH1Ref}>
              {editMode
                ? <EditField tag="span" className="hero-h1-l1" value={hero.line1} onChange={v => edit('hero','line1',v)} />
                : <span className="hero-h1-l1">{hero.line1}</span>}
              {editMode
                ? <EditField tag="span" className="hero-h1-l2" value={hero.line2} onChange={v => edit('hero','line2',v)} />
                : <span className="hero-h1-l2">{hero.line2}</span>}
            </h1>
            {editMode
              ? <EditField tag="p" className="hero-sub" style={{ '--ref': heroSubRef }} value={hero.subtitle} onChange={v => edit('hero','subtitle',v)} />
              : <p className="hero-sub" ref={heroSubRef}>{hero.subtitle}</p>}
            <div className="hero-btns">
              <button className="btn-primary" onClick={() => setShowBook(true)}>{t.btnBook}</button>
              <a href="#services" className="btn-ghost">{t.btnServices}</a>
              <button className="btn-pdf" onClick={() => {
                if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                  window.open('/PDFamko.pdf', '_blank');
                } else {
                  setShowPdf(true);
                }
              }} aria-label="Preview Company Profile PDF">{t.btnProfile}</button>
            </div>
            <div className="hero-stats">
              {(hero.stats || activeDefaults.hero.stats).map((s, i) => (
                <div key={i} className="hero-stat">
                  {editMode
                    ? <EditField className="hero-stat-num" value={s.num} onChange={v => { const a=[...(hero.stats||activeDefaults.hero.stats)]; a[i]={...a[i],num:v}; edit('hero','stats',a); }} />
                    : <span className="hero-stat-num">{s.num}</span>}
                  {editMode
                    ? <EditField className="hero-stat-label" value={s.label} onChange={v => { const a=[...(hero.stats||activeDefaults.hero.stats)]; a[i]={...a[i],label:v}; edit('hero','stats',a); }} />
                    : <span className="hero-stat-label">{s.label}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div className="hero-right">
            <div className="hero-logo-wrap">
              <div className="hero-logo-ring" />
              <div className="hero-logo-ring hero-logo-ring--2" />
              <img src="/logo.png" alt="AMKO Agency" className="hero-logo" />
            </div>
            <div className="hero-card">
              <span className="hero-card-label">{t.whyAmko}</span>
              <ul>
                {(hero.bullets || []).map((b, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                    {editMode
                      ? <><EditField value={b} style={{ flex: 1 }} onChange={v => { const a=[...hero.bullets]; a[i]=v; edit('hero','bullets',a); }} />
                          <button onClick={() => edit('hero','bullets',hero.bullets.filter((_,j)=>j!==i))} className="amko-remove-btn">✕</button></>
                      : b}
                  </li>
                ))}
              </ul>
              {editMode && <button className="amko-add-btn" onClick={() => edit('hero','bullets',[...(hero.bullets||[]),'New point'])}>+ Add point</button>}
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* ABOUT */}
      <section className="about-section" id="about">
        <div className="container">

          {/* HEADER */}
          <FadeInSection direction="up">
            <div className="about-hdr">
              <span className="section-tag">{t.aboutTag}</span>
              <h2>{t.aboutHeading}</h2>
              <div className="about-hdr-rule"><span /></div>
            </div>
          </FadeInSection>

          {/* MAIN GRID */}
          <div className="about-grid">

            {/* IMAGE COLUMN */}
            <FadeInSection direction="left" delay={100}>
              <div className="about-img-col">
                <div className="about-img-frame" style={{ transform: `translate(${cardPos.x}px,${cardPos.y}px)`, width: cardSize.w !== 640 ? cardSize.w : undefined, height: cardSize.h !== 520 ? cardSize.h : undefined }}>
                  <img src={about?.imageUrl || '/LogoBackground.png'} alt="AMKO Agency" className="about-img" />
                  {editMode && (
                    <label className="amko-img-overlay">
                      📷 Change Image
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => e.target.files[0] && uploadImg(e.target.files[0], 'about', 'imageUrl')} />
                    </label>
                  )}
                  <div className="about-img-veil" />
                  {/* floating stat badges */}
                  <div className="about-badge about-badge--tl">
                    <span className="about-badge-num">5+</span>
                    <span className="about-badge-lbl">{lang === 'ar' ? 'سنوات' : 'Years'}</span>
                  </div>
                  <div className="about-badge about-badge--br">
                    <span className="about-badge-num">100+</span>
                    <span className="about-badge-lbl">{lang === 'ar' ? 'مشروع' : 'Projects'}</span>
                  </div>
                  <div className="about-img-corner" />
                </div>
              </div>
            </FadeInSection>

            {/* TEXT COLUMN */}
            <FadeInSection direction="right" delay={180}>
              <div className="about-text-col">
                {editMode ? <EditField tag="p" className="about-lead" value={about.p1} onChange={v => edit('about','p1',v)} /> : <p className="about-lead">{about.p1}</p>}
                {editMode ? <EditField tag="p" className="about-body" value={about.p2} onChange={v => edit('about','p2',v)} /> : <p className="about-body">{about.p2}</p>}
                <div className="about-vm-row">
                  <div className="about-vm-card">
                    <div className="about-vm-icon">🎯</div>
                    <div className="about-vm-content">
                      <strong>{t.aboutVision}</strong>
                      {editMode ? <EditField tag="p" value={about.vision} onChange={v => edit('about','vision',v)} /> : <p>{about.vision}</p>}
                    </div>
                  </div>
                  <div className="about-vm-card about-vm-card--mission">
                    <div className="about-vm-icon">🚀</div>
                    <div className="about-vm-content">
                      <strong>{t.aboutMission}</strong>
                      {editMode ? <EditField tag="p" value={about.mission} onChange={v => edit('about','mission',v)} /> : <p>{about.mission}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>

          {/* VALUES */}
          <FadeInSection direction="up" delay={100}>
            <div className="about-values" style={{ transform: `translate(${valuesPos.x}px,${valuesPos.y}px)`, width: valuesW !== 100 ? `${valuesW}%` : undefined }}>
              <div className="about-values-hdr">
                <span className="about-values-line" />
                <h3>{t.ourValues}</h3>
                <span className="about-values-line" />
              </div>
              <div className="about-values-grid">
                {(about.values || []).map((v, i) => (
                  <div key={i} className="about-value-card" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {editMode
                      ? <><EditField value={v} style={{ flex: 1 }} onChange={val => { const a=[...about.values]; a[i]=val; edit('about','values',a); }} />
                          <button onClick={() => edit('about','values',about.values.filter((_,j)=>j!==i))} className="amko-remove-btn">✕</button></>
                      : v}
                  </div>
                ))}
              </div>
              {editMode && <button className="amko-add-btn" style={{ marginTop: 8 }} onClick={() => edit('about','values',[...(about.values||[]),'🆕 New Value'])}>+ Add Value</button>}
            </div>
          </FadeInSection>

        </div>
      </section>

      {/* SERVICES */}
      <FadeInSection direction="right">
      <section className="section section-white" id="services">
        <div className="container">
          <span className="section-tag">{t.servicesTag}</span>
          <h2>{t.servicesHeading}</h2>
          <p className="section-sub">{t.servicesSub}</p>
          {editMode ? (
            <div className="services-grid-full">
              {serviceItems.map((s, i) => (
                <div key={i} className="service-card-full" style={{ position: 'relative' }}>
                  {/* Image upload */}
                  <div className="service-card-media" style={{ position: 'relative' }}>
                    {(s.images?.[0] || s.imageUrl)
                      ? <img src={s.images?.[0] || s.imageUrl} alt={s.title} style={{ position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover' }} />
                      : <div className="service-card-no-img">{s.icon}</div>}
                    <div className="service-card-gradient" />
                    <label className="amko-img-overlay">
                      📷 Change Image
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => e.target.files[0] && uploadServiceImg(e.target.files[0], i)} />
                    </label>
                  </div>
                  {/* Text fields */}
                  <div className="service-card-body">
                    <div className="service-card-top">
                      <EditField className="service-icon-lg" value={s.icon} onChange={v => editService(i,'icon',v)} />
                      <EditField tag="h3" value={s.title} onChange={v => editService(i,'title',v)} />
                    </div>
                    <ul>
                      {(s.items || []).map((item, j) => (
                        <li key={j} style={{ display:'flex', alignItems:'center', gap:4 }}>
                          <span>—</span>
                          <EditField value={item} style={{ flex:1 }} onChange={v => editServiceItem(i,j,v)} />
                          <button onClick={() => { const its=(s.items||[]).filter((_,k)=>k!==j); editService(i,'items',its); }} className="amko-remove-btn">✕</button>
                        </li>
                      ))}
                    </ul>
                    <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
                      <button className="amko-add-btn" onClick={() => editService(i,'items',[...(s.items||[]),'New item'])}>+ Add item</button>
                      <button className="amko-remove-btn" style={{ padding:'0.25rem 0.7rem' }}
                        onClick={() => { const its=serviceItems.filter((_,k)=>k!==i); setContent(p => { const u={...p,services:{...p.services,items:its}}; notifySection('services',u.services); return u; }); }}>
                        Remove Card
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button className="amko-add-btn" style={{ marginTop:12 }}
                onClick={() => { const its=[...serviceItems,{icon:'🆕',title:'New Service',items:['Item 1']}]; setContent(p=>{const u={...p,services:{...p.services,items:its}};notifySection('services',u.services);return u;}); }}>
                + Add Service Card
              </button>
            </div>
          ) : (
            <div className="services-grid-full">
              {serviceItems.map((s, i) => (
                <ServiceCard key={i} s={s} onClick={() => setActiveService(s)} lang={lang} />
              ))}
            </div>
          )}
        </div>
      </section>
      </FadeInSection>

      {/* PROCESS */}
      <FadeInSection direction="up">
      <section className="process-section" id="process">
        <div className="container">
          <div className="process-hdr">
            <span className="section-tag light">{t.processTag}</span>
            <h2 className="light">{t.processHeading}</h2>
          </div>
          <div className="process-track">
            {(proc?.steps || []).map((p, i) => (
              <div key={p.n+i} className="process-node" style={{ position: 'relative' }}>
                {editMode && (
                  <button onClick={() => edit('process','steps',(proc?.steps||[]).filter((_,j)=>j!==i))} className="amko-remove-btn" style={{ position:'absolute',top:-6,right:-6,zIndex:10,borderRadius:'50%',width:18,height:18,padding:0,display:'flex',alignItems:'center',justifyContent:'center',background:'#dc2626',border:'none',color:'#fff',fontSize:'0.6rem',cursor:'pointer' }}>✕</button>
                )}
                <div className="process-node-top">
                  <div className="process-bubble">
                    {editMode
                      ? <EditField className="process-bubble-n" value={p.n} onChange={v => { const a=[...(proc?.steps||[])]; a[i]={...a[i],n:v}; edit('process','steps',a); }} />
                      : <span className="process-bubble-n">{p.n}</span>}
                  </div>
                  {i < (proc?.steps?.length ?? 0) - 1 && <div className="process-connector" />}
                </div>
                <div className="process-node-body">
                  {editMode
                    ? <EditField tag="div" className="process-node-title" value={p.title} onChange={v => { const a=[...(proc?.steps||[])]; a[i]={...a[i],title:v}; edit('process','steps',a); }} />
                    : <div className="process-node-title">{p.title}</div>}
                  {editMode
                    ? <EditField tag="div" className="process-node-desc" value={p.desc} onChange={v => { const a=[...(proc?.steps||[])]; a[i]={...a[i],desc:v}; edit('process','steps',a); }} />
                    : <div className="process-node-desc">{p.desc}</div>}
                </div>
              </div>
            ))}
          </div>
          {editMode && (
            <button className="amko-add-btn" style={{ marginTop:16, color:'#fff', borderColor:'rgba(255,255,255,0.4)' }}
              onClick={() => { const steps=proc?.steps||[]; edit('process','steps',[...steps,{n:`0${steps.length+1}`,title:'New Step',desc:'Description'}]); }}>
              + Add Step
            </button>
          )}
        </div>
      </section>
      </FadeInSection>

      {/* PORTFOLIO */}
      <FadeInSection direction="left">
      <section className="portfolio-section" id="portfolio">
        <div className="portfolio-bg-text" aria-hidden="true">WORK</div>
        <div className="container">
          <div className="portfolio-hdr">
            <div className="portfolio-hdr-left">
              <span className="section-tag">{t.portfolioTag}</span>
              <h2>{t.portfolioHeading}</h2>
            </div>
            <div className="portfolio-hdr-right">
              <span className="portfolio-count">{filteredPortfolio.length > 0 ? `${filteredPortfolio.length} ${lang === 'ar' ? 'مشروع' : 'Projects'}` : ''}</span>
              <p className="portfolio-hdr-sub">{t.portfolioSub}</p>
            </div>
          </div>

          {filteredPortfolio.length > 0 ? (
            <PortfolioCoverflow items={filteredPortfolio} onSelect={setActivePortfolio} />
          ) : (
            <div className="portfolio-empty">
              <div className="empty-icon">🖼️</div>
              <p>{t.portfolioEmpty}</p>
              <span>{t.portfolioEmptySub}</span>
            </div>
          )}
        </div>
      </section>
      </FadeInSection>

      {/* SECTION BREAK */}
      <div className="section-break" aria-hidden="true">
        <span className="section-break-line" />
        <span className="section-break-dot" />
        <span className="section-break-dot" />
        <span className="section-break-dot" />
        <span className="section-break-line" />
      </div>

      {/* CLIENTS */}
      {showClients && (
      <FadeInSection direction="right">
      <section className="clients-section">
        <div className="container">
          <div className="clients-hdr">
            <span className="section-tag">{t.clientsTag}</span>
            <h2>{t.clientsHeading}</h2>
          </div>
          <div className="clients-grid-new">
            {(clients?.list || []).map((c, i) => (
              <div key={c+i} className="client-card" style={{ position:'relative' }}>
                <span className="client-card-idx">0{i + 1}</span>
                {editMode
                  ? <EditField className="client-card-name" value={c} onChange={v => { const a=[...(clients?.list||[])]; a[i]=v; edit('clients','list',a); }} />
                  : <span className="client-card-name">{c}</span>}
                {editMode && <button onClick={() => edit('clients','list',(clients?.list||[]).filter((_,j)=>j!==i))} className="amko-remove-btn" style={{ position:'absolute',top:4,right:4 }}>✕</button>}
              </div>
            ))}
            {editMode && (
              <div className="client-card" style={{ cursor:'pointer', border:'1.5px dashed rgba(255,255,255,0.3)', justifyContent:'center' }}
                onClick={() => edit('clients','list',[...(clients?.list||[]),'New Client'])}>
                <span className="client-card-name" style={{ color:'rgba(255,255,255,0.5)' }}>+ Add Client</span>
              </div>
            )}
          </div>
        </div>
      </section>
      </FadeInSection>
      )}

      {/* REVIEWS */}
      <FadeInSection direction="up">
      <section className="reviews-section" id="reviews">
        <div className="container">
          <div className="reviews-hdr">
            <div>
              <h2>{t.reviewsHeading}</h2>
            </div>
            <div className="reviews-score">
              <span className="reviews-score-num">4.9</span>
              <div>
                <div className="stars">★★★★★</div>
                <div className="reviews-score-count">{t.reviewsCount}</div>
              </div>
            </div>
          </div>
          {displayReviews.length > 0 && (
            <div className="reviews-grid-new">
              {displayReviews.map(r => (
                <div key={r.id} className="review-card-new">
                  <div className="review-card-quote">"</div>
                  <p className="review-card-text">{r.text}</p>
                  <div className="review-card-footer">
                    <div className="review-card-stars">{'★'.repeat(r.rating || 5)}</div>
                    <div className="review-card-author">
                      <div className="review-card-avatar">{r.name.split(' ').map(w => w[0]).join('').slice(0,2)}</div>
                      <div>
                        <div className="review-card-name">{r.name}</div>
                        {r.role && <div className="review-card-role">{r.role}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      </FadeInSection>

      {/* BREAK — before Leave a Review */}
      <div className="section-break" aria-hidden="true">
        <span className="section-break-line" />
        <span className="section-break-dot" />
        <span className="section-break-dot" />
        <span className="section-break-dot" />
        <span className="section-break-line" />
      </div>

      {/* LEAVE A REVIEW */}
      <FadeInSection direction="up">
      <section className="leave-review-section">
        <div className="container container-sm">
          <div className="leave-review-hdr">
            <h2>{t.leaveReview}</h2>
            <p>{t.leaveReviewSub}</p>
          </div>
          {reviewSent ? (
            <div className="leave-review-success">
              <div className="leave-review-success-icon">🙏</div>
              <h3>{t.reviewThanks}</h3>
              <p>{t.reviewThanksMsg}</p>
            </div>
          ) : (
            <div className="leave-review-form-wrap">
              <form onSubmit={submitReview} className="form">
                <div className="form-row">
                  <input placeholder={t.reviewNamePh} required value={reviewForm.name} onChange={e => setReviewForm(p => ({ ...p, name: e.target.value }))} />
                  <input placeholder={t.reviewRolePh} value={reviewForm.role} onChange={e => setReviewForm(p => ({ ...p, role: e.target.value }))} />
                </div>
                <div className="rating-select">
                  <label>{t.reviewRatingLabel}</label>
                  <div className="stars-input">
                    {[1,2,3,4,5].map(n => (
                      <button type="button" key={n} className={`star-btn ${n <= reviewForm.rating ? 'active' : ''}`} onClick={() => setReviewForm(p => ({ ...p, rating: n }))}>★</button>
                    ))}
                  </div>
                </div>
                <textarea rows={4} placeholder={t.reviewTextPh} required value={reviewForm.text} onChange={e => setReviewForm(p => ({ ...p, text: e.target.value }))} />
                <button type="submit" className="btn-primary">{t.reviewSubmit}</button>
              </form>
            </div>
          )}
        </div>
      </section>
      </FadeInSection>

      {/* FAQ */}
      <FadeInSection direction="up">
      <section className="faq-section">
        <div className="container container-sm">
          <div className="faq-hdr">
            <span className="section-tag">{t.faqTag}</span>
            <h2>{t.faqHeading}</h2>
          </div>
          <div className="faq-list-new">
            {(faqs?.items || []).map((f, i) => (
              <div key={i} className={`faq-item-new ${faqOpen === i || editMode ? 'open' : ''}`} style={{ position:'relative' }}>
                {editMode && <button onClick={() => edit('faqs','items',(faqs?.items||[]).filter((_,j)=>j!==i))} className="amko-remove-btn" style={{ position:'absolute',top:12,right:12,zIndex:5 }}>✕</button>}
                <button className="faq-q-new" onClick={() => !editMode && setFaqOpen(faqOpen === i ? null : i)}>
                  <span className="faq-num">0{i + 1}</span>
                  {editMode
                    ? <EditField tag="span" className="faq-question" value={f.q} onChange={v => { const a=[...(faqs?.items||[])]; a[i]={...a[i],q:v}; edit('faqs','items',a); }} />
                    : <span className="faq-question">{f.q}</span>}
                  {!editMode && <span className="faq-icon">{faqOpen === i ? '−' : '+'}</span>}
                </button>
                {editMode
                  ? <EditField tag="div" className="faq-a-new" value={f.a} onChange={v => { const a=[...(faqs?.items||[])]; a[i]={...a[i],a:v}; edit('faqs','items',a); }} />
                  : faqOpen === i && <div className="faq-a-new">{f.a}</div>}
              </div>
            ))}
          </div>
          {editMode && (
            <button className="amko-add-btn" style={{ marginTop:12 }}
              onClick={() => edit('faqs','items',[...(faqs?.items||[]),{q:'New Question?',a:'Answer here.'}])}>
              + Add FAQ
            </button>
          )}
        </div>
      </section>
      </FadeInSection>

      {/* CONTACT */}
      <FadeInSection direction="up">
      <section className="contact-section" id="contact">
        <div className="contact-bg-deco" aria-hidden="true" />
        <div className="container">
          <div className="contact-hdr">
            <span className="section-tag light">{t.contactTag}</span>
            <h2 className="light">{t.contactHeading}</h2>
            <p className="contact-hdr-sub">{t.contactSub}</p>
          </div>

          <div className="contact-grid">
            {/* LEFT — info */}
            <div className="contact-info-panel">
              <div className="contact-avail">
                <span className="contact-avail-dot" />
                {lang === 'ar' ? 'نرد خلال 24 ساعة' : 'We respond within 24 hours'}
              </div>

              <div className="contact-cards">
                <a href={`tel:${(contact?.phone || activeDefaults.contact.phone).replace(/\s/g,'')}`} className="contact-card">
                  <div className="contact-card-icon-wrap contact-card-icon-wrap--phone">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.58 3.4 2 2 0 0 1 3.55 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.5a16 16 0 0 0 6 6l.87-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </div>
                  <div className="contact-card-body">
                    <span className="contact-card-label">{t.contactPhone}</span>
                    {editMode
                      ? <EditField tag="span" className="contact-card-value" value={contact?.phone || activeDefaults.contact.phone} onChange={v => edit('contact','phone',v)} />
                      : <span className="contact-card-value">{contact?.phone || activeDefaults.contact.phone}</span>}
                  </div>
                  <span className="contact-card-arrow">↗</span>
                </a>

                <a href={`mailto:${contact?.email || activeDefaults.contact.email}`} className="contact-card">
                  <div className="contact-card-icon-wrap contact-card-icon-wrap--email">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </div>
                  <div className="contact-card-body">
                    <span className="contact-card-label">{t.contactEmail}</span>
                    {editMode
                      ? <EditField tag="span" className="contact-card-value" value={contact?.email || activeDefaults.contact.email} onChange={v => edit('contact','email',v)} />
                      : <span className="contact-card-value">{contact?.email || activeDefaults.contact.email}</span>}
                  </div>
                  <span className="contact-card-arrow">↗</span>
                </a>

                <div className="contact-card contact-card--static">
                  <div className="contact-card-icon-wrap contact-card-icon-wrap--loc">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </div>
                  <div className="contact-card-body">
                    <span className="contact-card-label">{t.contactLocation}</span>
                    {editMode
                      ? <EditField tag="span" className="contact-card-value" value={contact?.location || activeDefaults.contact.location} onChange={v => edit('contact','location',v)} />
                      : <span className="contact-card-value">{contact?.location || activeDefaults.contact.location}</span>}
                  </div>
                </div>
              </div>

              <div className="contact-social-row">
                <a href={contact?.whatsapp || activeDefaults.contact.whatsapp} target="_blank" rel="noreferrer" className="csocial-btn csocial-wa" aria-label="WhatsApp">
                  <svg viewBox="0 0 32 32" width="18" height="18" fill="currentColor"><path d="M16 0C7.164 0 0 7.163 0 16c0 2.822.736 5.463 2.024 7.754L0 32l8.469-2.001A15.93 15.93 0 0 0 16 32c8.836 0 16-7.163 16-16S24.836 0 16 0zm7.27 22.516c-.398-.199-2.354-1.162-2.72-1.294-.365-.133-.631-.199-.897.199-.265.398-1.029 1.294-1.261 1.56-.232.266-.465.299-.863.1-.398-.2-1.681-.619-3.202-1.976-1.183-1.056-1.981-2.361-2.213-2.759-.232-.398-.025-.613.175-.811.18-.178.398-.465.597-.698.2-.232.266-.398.399-.664.132-.265.066-.498-.034-.697-.1-.2-.897-2.162-1.229-2.959-.324-.777-.653-.672-.897-.684l-.764-.013c-.265 0-.697.1-1.062.498-.365.398-1.394 1.362-1.394 3.323s1.427 3.854 1.626 4.12c.199.265 2.807 4.285 6.802 6.011.951.41 1.693.655 2.271.839.954.304 1.823.261 2.51.158.765-.114 2.354-.963 2.686-1.892.332-.93.332-1.727.232-1.892-.099-.166-.365-.265-.763-.464z"/></svg>
                  <span>WhatsApp</span>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noreferrer" className="csocial-btn csocial-ig" aria-label="Instagram">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
                  <span>Instagram</span>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noreferrer" className="csocial-btn csocial-fb" aria-label="Facebook">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.026 1.792-4.697 4.533-4.697 1.312 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.932-1.956 1.886v2.266h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>
                  <span>Facebook</span>
                </a>
              </div>
            </div>

            {/* RIGHT — form */}
            <div className="contact-form-panel">
              <div className="contact-form-accent" />
              <ConsultationForm services={serviceItems.map(s => s.title)} />
            </div>
          </div>
        </div>
      </section>
      </FadeInSection>

      {/* BOOK MODAL */}
      {showBook && (
        <div className="modal-overlay" onClick={() => setShowBook(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowBook(false)}>✕</button>
            {bookSent ? (
              <div className="modal-success">
                <div className="success-icon">✅</div>
                <h3>{t.bookReceived}</h3>
                <p>{t.bookConfirm}</p>
                <button className="btn-outline" onClick={() => { setShowBook(false); setBookSent(false); }}>{t.bookClose}</button>
              </div>
            ) : (
              <>
                <h2>{t.bookHeading}</h2>
                <p className="modal-sub">{t.bookSub}</p>
                <form onSubmit={submitBook} className="form">
                  <div className="form-row">
                    <input placeholder={t.bookFullName} required value={bookForm.fullName} onChange={e => setBookForm(p => ({ ...p, fullName: e.target.value }))} />
                    <input type="email" placeholder={t.bookEmail} required value={bookForm.email} onChange={e => setBookForm(p => ({ ...p, email: e.target.value }))} />
                  </div>
                  <input type="tel" placeholder={t.bookPhone} required value={bookForm.phone} onChange={e => setBookForm(p => ({ ...p, phone: e.target.value }))} />
                  <select required value={bookForm.service} onChange={e => setBookForm(p => ({ ...p, service: e.target.value }))}>
                    <option value="">{t.bookSelectService}</option>
                    {serviceItems.map(s => <option key={s.title} value={s.title}>{s.title}</option>)}
                  </select>
                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label">{t.bookDate}</label>
                      <input type="date" required value={bookForm.date} min={new Date().toISOString().split('T')[0]} onChange={e => setBookForm(p => ({ ...p, date: e.target.value }))} />
                    </div>
                    <div className="form-field">
                      <label className="form-label">{t.bookTime}</label>
                      <input type="time" required value={bookForm.time} onChange={e => setBookForm(p => ({ ...p, time: e.target.value }))} />
                    </div>
                  </div>
                  <textarea rows={3} placeholder={t.bookNotes} value={bookForm.notes} onChange={e => setBookForm(p => ({ ...p, notes: e.target.value }))} />
                  <button type="submit" className="btn-primary">{t.bookSubmit}</button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {activeService && <ServiceModal service={activeService} onClose={closeService} />}
      {activePortfolio && <PortfolioModal item={activePortfolio} onClose={() => setActivePortfolio(null)} />}

      {/* PDF PREVIEW MODAL */}
      {showPdf && (
        <div className="pdf-overlay" onClick={() => setShowPdf(false)} role="dialog" aria-modal="true" aria-label="Company Profile PDF">
          <div className="pdf-panel" onClick={e => e.stopPropagation()}>
            <div className="pdf-panel-header">
              <div className="pdf-panel-title">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t.pdfTitle}</span>
              </div>
              <div className="pdf-panel-actions">
                <button className="pdf-close-btn" onClick={() => setShowPdf(false)} aria-label="Close PDF preview">✕</button>
              </div>
            </div>
            <div className="pdf-panel-body">
              <PdfViewer />
            </div>
          </div>
        </div>
      )}

      {/* WHATSAPP FLOAT */}
      <a href={contact?.whatsapp || activeDefaults.contact.whatsapp} target="_blank" rel="noreferrer" className="whatsapp-btn" aria-label="WhatsApp">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="26" height="26" fill="#fff">
          <path d="M16 0C7.164 0 0 7.163 0 16c0 2.822.736 5.463 2.024 7.754L0 32l8.469-2.001A15.93 15.93 0 0 0 16 32c8.836 0 16-7.163 16-16S24.836 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.769-1.853l-.485-.288-5.026 1.188 1.216-4.898-.317-.502A13.236 13.236 0 0 1 2.667 16C2.667 8.637 8.637 2.667 16 2.667S29.333 8.637 29.333 16 23.363 29.333 16 29.333zm7.27-9.817c-.398-.199-2.354-1.162-2.72-1.294-.365-.133-.631-.199-.897.199-.265.398-1.029 1.294-1.261 1.56-.232.266-.465.299-.863.1-.398-.2-1.681-.619-3.202-1.976-1.183-1.056-1.981-2.361-2.213-2.759-.232-.398-.025-.613.175-.811.18-.178.398-.465.597-.698.2-.232.266-.398.399-.664.132-.265.066-.498-.034-.697-.1-.2-.897-2.162-1.229-2.959-.324-.777-.653-.672-.897-.684l-.764-.013c-.265 0-.697.1-1.062.498-.365.398-1.394 1.362-1.394 3.323s1.427 3.854 1.626 4.12c.199.265 2.807 4.285 6.802 6.011.951.41 1.693.655 2.271.839.954.304 1.823.261 2.51.158.765-.114 2.354-.963 2.686-1.892.332-.93.332-1.727.232-1.892-.099-.166-.365-.265-.763-.464z"/>
        </svg>
      </a>
    </div>
  );
}
